<template>
  <div class="wrapper">
    <div class="createStarterTeam" :class="{ colorBg: current === STEPS.STEP2 }">
      <Step0Left v-if="current === STEPS.STEP0" class="step-0-left" />
      <Step1Left v-if="current === STEPS.STEP1" class="w-[40%] h-full" />
      <div class="step-0-right">
        <Steps class="steps" :current="current" />
        <Step0Content v-if="current === STEPS.STEP0" @next="nextStep" />
        <Step1Content v-if="current === STEPS.STEP1" @next="nextStep" @account="onAccountGet" />
        <Step2Content
          v-if="current === STEPS.STEP2 && starterOrgRes"
          :starter-org-res="starterOrgRes"
          @paySuccess="handlePaySuccess"
        />
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { getUserToken } from '~/api';
import type { CreateStarterOrgResponse, LoginInfo } from '~/api/interface';
import { useChangeUser } from '~/composables/user';
import { BROADCAST_KEY } from '~/constants/config';
import { STEPS, TEAM_STARTER_PAY_ACCOUNT_KEY } from '~/constants/teamCreate';
import { useClient } from '~/hooks/useClient';
import { useHomeRoute } from '~/utils/permission';
import { buriedLogin } from '~/utils/track';
import Step0Left from './components/ContentLeft.vue';
import Step0Content from './components/starter/step0/content.vue';
import Step1Content from './components/starter/step1/content.vue';
import Step1Left from './components/starter/step1/left.vue';
import Step2Content from './components/starter/step2/content.vue';
import Steps from './components/starter/steps/index.vue';

const { setSession } = useChangeUser();

const route = useRoute();
const router = useRouter();
const session = useSession();
const homeRoute = useHomeRoute();

const current = ref<STEPS>(Number(route.query.step) || STEPS.STEP0);
const starterOrgRes = useClient<CreateStarterOrgResponse>(() =>
  sessionGetItem(TEAM_STARTER_PAY_ACCOUNT_KEY),
);

function updateCustomSetting(userInfo: any) {
  CustomSettingApp.initAvatar(userInfo.logo_url, userInfo.default_logo_url);
  CustomSettingApp.initLogo(userInfo.platform_logo_url, userInfo.default_platform_logo_url);
}

const handlePaySuccess = async () => {
  if (!starterOrgRes.value?.user_id) {
    return;
  }
  trackEvent(TRACK_EVENT.TEAM_PAYMENT_SUCCESS);
  const { data } = await getUserToken(starterOrgRes.value!.user_id); // 获取LoginInfo
  const account = data?.[0] as LoginInfo;
  buriedLogin(Number(account.user_id));
  await setSession(account); // 更新当前账户
  updateCustomSetting(account); // 更新头像和logo
  emitBroadcast(BROADCAST_KEY.REFRESH, { tabId: TAB_ID });
  sessionRemoveItem(TEAM_STARTER_PAY_ACCOUNT_KEY);

  router.push({
    ...homeRoute,
    query: {
      thanks: 1,
    },
  });
};

function onAccountGet(info: CreateStarterOrgResponse) {
  sessionSetItem(TEAM_STARTER_PAY_ACCOUNT_KEY, info);
  starterOrgRes.value = info;
}

watch(
  () => route.query.step,
  step => {
    current.value = Number(step) || STEPS.STEP0;
  },
  { immediate: true },
);

watch(
  () => session.value,
  user => {
    if (!user?.user_id && current.value !== STEPS.STEP0) {
      router.replace({
        query: { step: STEPS.STEP0 },
        name: route.name as string,
      });
    }
  },
  { immediate: true },
);

function nextStep() {
  router.push({
    query: { step: current.value + 1 },
    name: route.name as string,
  });
}
</script>
<style lang="scss" scoped>
.wrapper {
  width: 100vw;
  overflow: auto;
  background: var(--create-wrapper-bg);
  min-height: calc(100vh - 63px);
}

.step-0-left {
  width: 40%;
  height: calc(100vh - 63px);
}

.step-0-right {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 63px);
  height: 100%;
  flex: 1;
}

.createStarterTeam {
  display: flex;
  width: 100%;
  overflow-x: hidden;
  align-items: center;

  .steps {
    width: 450px;
    margin-bottom: 20px !important;
    transform: translateX(-70px);
  }
}

.colorBg {
  background: var(--create-bg);
}

:deep(.ant-steps.ant-steps-small .ant-steps-item-tail) {
  top: -8px;
}
</style>
